import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { AnalogClock } from '../../../visuals';
import { Clock as ClockIcon, MousePointer2, Sun, Moon, Sunrise, Sunset } from 'lucide-react';
import { error as logError } from '../../../utils/logger';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
type ActiveMode = 'hours' | 'minutes';
/** Map a 24-hour value to a day-part with icon + Dutch label.
 *  Drives the visual sun/moon context — the seed's `target.hours` (24h) is the
 *  single source of truth for which time of day a question refers to. */
function getDayPart(hours24: number): {
  label: string;
  Icon: typeof Sun;
  iconClass: string;
  bgClass: string;
  textClass: string;
} {
  const h = (hours24 % 24 + 24) % 24;
  if (h >= 6 && h < 12) {
    return {
      label: 'ochtend',
      Icon: Sunrise,
      iconClass: 'text-amber-500',
      bgClass: 'bg-amber-50 border-amber-200',
      textClass: 'text-amber-700'
    };
  }
  if (h >= 12 && h < 18) {
    return {
      label: 'middag',
      Icon: Sun,
      iconClass: 'text-orange-500',
      bgClass: 'bg-orange-50 border-orange-200',
      textClass: 'text-orange-700'
    };
  }
  if (h >= 18 && h < 22) {
    return {
      label: 'avond',
      Icon: Sunset,
      iconClass: 'text-rose-500',
      bgClass: 'bg-rose-50 border-rose-200',
      textClass: 'text-rose-700'
    };
  }
  return {
    label: 'nacht',
    Icon: Moon,
    iconClass: 'text-indigo-400',
    bgClass: 'bg-indigo-50 border-indigo-200',
    textClass: 'text-indigo-700'
  };
}
/** Responsive clock size hook — reacts to viewport, no SSR break. */
function useClockSize() {
  const [size, setSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 220;
    const vw = window.innerWidth;
    if (vw < 360) return 180;
    if (vw < 420) return 200;
    if (vw < 640) return 220;
    return 240;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      const vw = window.innerWidth;
      if (vw < 360) setSize(180);else if (vw < 420) setSize(200);else if (vw < 640) setSize(220);else setSize(240);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return size;
}
export function ClockSetRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const clockConfig = question.data?.clock;
  const targetHours = clockConfig?.target?.hours ?? 0;
  const targetMinutes = clockConfig?.target?.minutes ?? 0;
  // Analog clock is 12-hour by nature: show target in 12-hour for display
  const targetHours12 = targetHours % 12 || 12;
  const targetHoursDisplay = String(targetHours12).padStart(2, '0');
  const targetMinutesDisplay = String(targetMinutes).padStart(2, '0');
  // Day-part driven by the seed's 24-hour value
  const dayPart = useMemo(() => getDayPart(targetHours), [targetHours]);
  // Local state — always in 12-hour clock domain (0-11). Start at 12 (= 0).
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [activeMode, setActiveMode] = useState<ActiveMode>('hours');
  const [hasInteracted, setHasInteracted] = useState(false);
  const hoursMatchedRef = useRef(false);
  const clockSize = useClockSize();
  // Reset state when question changes
  useEffect(() => {
    setHours(0);
    setMinutes(0);
    setActiveMode('hours');
    setHasInteracted(false);
    hoursMatchedRef.current = false;
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(hasInteracted);
  }, [hasInteracted, onReadyChange]);
  // Compute answer in HH:MM 24-hour format using the question's target as the
  // "AM/PM context" — since analog has no AM/PM, the validator compares modulo 12.
  const answerPayload = useMemo(() => {
    const offset = targetHours >= 12 ? 12 : 0;
    const h = hours % 12 + offset;
    return `${String(h).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }, [hours, minutes, targetHours]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  if (!clockConfig || !clockConfig.target) {
    logError('ClockSetRenderer: Missing clock config in question.data.clock');
    return <div className="text-center p-6 bg-red-50 rounded-2xl border-2 border-red-200" data-id="element-11901" data-mp="2o227nyld8hjt">
        <p className="text-red-700 font-bold" data-id="element-11902" data-mp="2aaxf1ggf0piz">⚠️ Klok configuratie ontbreekt</p>
      </div>;
  }
  const handleClockChange = (newH: number, newM: number) => {
    setHasInteracted(true);
    if (newH !== hours) setHours(newH);
    if (newM !== minutes) setMinutes(newM);
  };
  // Smart auto-switch from hours → minutes once hours are correct
  useEffect(() => {
    if (!hasInteracted) return;
    if (activeMode !== 'hours') return;
    if (hours % 12 === targetHours % 12 && !hoursMatchedRef.current) {
      hoursMatchedRef.current = true;
      const t = setTimeout(() => setActiveMode('minutes'), 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [hours, hasInteracted, activeMode, targetHours]);
  const hoursValueDisplay = String(hours === 0 ? 12 : hours).padStart(2, '0');
  const minutesValueDisplay = String(minutes).padStart(2, '0');
  const hoursCorrect = hours % 12 === targetHours % 12;
  const minutesCorrect = minutes === targetMinutes;
  const DayPartIcon = dayPart.Icon;
  return <div className="flex flex-col items-center w-full max-w-xl mx-auto px-1 sm:px-4 space-y-4 sm:space-y-6" data-id="element-11903" data-mp="2eegctg36m8kv">
      {/* Target time display + day-part badge */}
      <div className="flex flex-col items-center gap-2" data-id="element-11904" data-mp="29hq114yetz59">
        <p className="text-[11px] sm:text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-11905" data-mp="2ngfxqjhbhwt3">
          Zet de klok op
        </p>
        <div className="inline-flex items-baseline gap-0.5 sm:gap-1 text-3xl sm:text-5xl font-black text-slate-900 tracking-wider tabular-nums bg-white px-4 sm:px-7 py-2 sm:py-3 rounded-xl sm:rounded-2xl shadow-sm border-2 border-slate-100" data-id="element-11906" data-mp="2jfnk7e1ex5r0">
          <span className={`transition-colors ${activeMode === 'hours' ? 'text-indigo-600' : 'text-slate-900'}`} data-id="element-11907" data-mp="30engpqlxvnj9">
            
            {targetHoursDisplay}
          </span>
          <span className="text-slate-400" data-id="element-11908" data-mp="397ioaqyvpvc7">:</span>
          <span className={`transition-colors ${activeMode === 'minutes' ? 'text-indigo-600' : 'text-slate-900'}`} data-id="element-11909" data-mp="396pc3ah5idmo">
            
            {targetMinutesDisplay}
          </span>
        </div>
        <motion.div key={dayPart.label} initial={{
        opacity: 0,
        y: -2,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${dayPart.bgClass}`} aria-label={`Tijd van de dag: ${dayPart.label}`} data-id="element-11910" data-mp="3j6pxd1ykz3ti">
          
          <DayPartIcon className={`w-3.5 h-3.5 ${dayPart.iconClass}`} data-id="element-11911" data-mp="awx7xk66cc9x" />
          <span className={`text-[11px] sm:text-xs font-bold ${dayPart.textClass}`} data-id="element-11912" data-mp="63uj1pccdr02">
            
            {dayPart.label}
          </span>
        </motion.div>
      </div>

      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 w-full max-w-sm" data-id="element-11913" data-mp="3tawnm8uxgfs5">
        <ModeButton active={activeMode === 'hours'} onClick={() => setActiveMode('hours')} label="Uren" value={hoursValueDisplay} isCorrect={hoursCorrect && hasInteracted} data-id="element-11914" data-mp="tuzzo6q3wnbf" />
        
        <ModeButton active={activeMode === 'minutes'} onClick={() => setActiveMode('minutes')} label="Minuten" value={minutesValueDisplay} isCorrect={minutesCorrect && hasInteracted} data-id="element-11915" data-mp="kttkc0z71ijr" />
        
      </div>

      {/* Interactive clock */}
      <div className="relative p-3 sm:p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-[1.5rem] sm:rounded-[2.5rem] border border-slate-200 shadow-inner" data-id="element-11916" data-mp="35l297muymj5s">
        <AnalogClock hours={hours} minutes={minutes} showSeconds={false} size={clockSize} interactive activeMode={activeMode} minuteStep={clockConfig.analog?.minuteStep ?? 1} onChange={handleClockChange} data-id="element-11917" data-mp="14i2vkgz0s1h1" />
        
      </div>

      {/* Hint */}
      <AnimatePresence mode="wait" data-id="element-11918" data-mp="ukx0yyr75sc2">
        <motion.div key={activeMode + String(hasInteracted)} initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.2
      }} className="flex items-center gap-1.5 sm:gap-2 text-slate-600 bg-white px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full border border-slate-200 shadow-sm max-w-[94vw]" data-id="element-11919" data-mp="ulzulefwm8vo">
          
          <MousePointer2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0" data-id="element-11920" data-mp="1igsqpr2a3r2r" />
          <span className="text-[11px] sm:text-sm font-medium leading-tight" data-id="element-11921" data-mp="266wupzjdpsua">
            {!hasInteracted ? `Tik of sleep om de ${activeMode === 'hours' ? 'uurwijzer' : 'minutenwijzer'} te zetten` : activeMode === 'hours' ? 'Zet de uurwijzer op het juiste uur' : 'Zet de minutenwijzer op het juiste getal'}
          </span>
        </motion.div>
      </AnimatePresence>
    </div>;
}
interface ModeButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  value: string;
  isCorrect: boolean;
}
function ModeButton({
  active,
  onClick,
  label,
  value,
  isCorrect
}: ModeButtonProps) {
  return <motion.button type="button" onClick={onClick} whileTap={{
    scale: 0.96
  }} className={`relative flex items-center justify-between gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3.5 min-h-[52px] sm:min-h-[56px] rounded-xl sm:rounded-2xl border-2 transition-all ${active ? 'bg-indigo-600 border-indigo-700 text-white shadow-lg shadow-indigo-200' : 'bg-white border-slate-200 text-slate-700 active:border-slate-300 sm:hover:border-slate-300'}`} data-id="element-11922" data-mp="3comxf2mflz5n">
      
      <div className="flex items-center gap-1 sm:gap-2 min-w-0" data-id="element-11923" data-mp="111q9usuwup2s">
        <ClockIcon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${active ? 'text-indigo-200' : 'text-slate-400'}`} data-id="element-11924" data-mp="wxquwepa3ybj" />
        
        <span className="text-xs sm:text-base font-bold truncate" data-id="element-11925" data-mp="ksw7b16h37t">{label}</span>
      </div>
      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0" data-id="element-11926" data-mp="3sove8qixzx11">
        <span className={`text-lg sm:text-2xl font-black tabular-nums ${active ? 'text-white' : 'text-slate-900'}`} data-id="element-11927" data-mp="3nuhiuo4a5pwd">
          
          {value}
        </span>
        {isCorrect && <motion.span initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 18
      }} className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${active ? 'bg-emerald-300' : 'bg-emerald-500'} shadow-sm`} data-id="element-11928" data-mp="3dyxnc2tmot85" />}
      </div>
    </motion.button>;
}